import React, { useEffect, useState } from "react";
import {
  Alert,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  TextField,
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import numericValidator from "services/numericValidator";
import {
  _addScenario,
  _getScenarioByLocationID,
} from "middlewares/DashboardApi/scenarioView";
import { ScenarioModel } from "./data/ScenarioModel";
import Spinner from "components/Spinner";
import { Row, Scenario } from "./data/Scenario";
import EmptyState from "./EmptyState";
import ScenarioTableRow from "./ScenarioTableRow";
import ScenarioViewHeader from "./ScenarioViewHeader";
import { useAppState } from "context/appState.context";
import { TitleDefinitionConstants, UserRoles } from "constants/constant";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

/**
 * A displayed scenario column. `key` is an internal ordinal identifier used to
 * keep duplicate section names distinct; `label` is the section name shown to
 * the user.
 */
interface ScenarioColumn {
  key: string;
  label: string;
}

const buildScenarioRow = (
  scenarios: any[],
  columns: ScenarioColumn[]
): Row => {
  const scenarioData: { [key: string]: Scenario } = {};
  scenarios.forEach((scenario: any) => {
    const column = columns.find(
      (candidate) =>
        candidate.label === (scenario.scenarioSection ?? "") &&
        !scenarioData[candidate.key]
    );
    if (!column) {
      return;
    }
    const {
      scenarioId,
      startYear,
      execution,
      pace,
      scenarioSection,
      reductionMeasureId,
    } = scenario;
    scenarioData[column.key] = {
      scenarioId,
      startYear,
      execution,
      pace,
      scenarioSection,
      reductionMeasureId,
    };
  });

  return {
    item: scenarios[0].reductionMeasureName,
    ...scenarioData,
  };
};

/**
 * ScenarioView component handles the display and management of scenarios
 * related to the Net Zero Decarbonization Model for a specific location.
 *
 * @returns {JSX.Element} The ScenarioView component.
 */
const ScenarioView = () => {
  const { LocationId } = useParams();
  const { userRole } = useAppState();

  // State variables for executionPercentage and peace
  const [execution, setExecution] = useState<{ [key: string]: string }>({});
  const [peace, setPeace] = useState<{ [key: string]: string }>({});
  const [rows, setRows] = useState<Row[]>([]);
  const [scenarioKeys, setScenarioKeys] = useState<string[]>([]);
  const [startYear, setStartYear] = useState<{ [key: string]: string }>({});
  const [responseState, setResponseState] = useState<Array<ScenarioModel>>([]);
  const [loading, setLoading] = useState(true);
  const [invalidInput, setInvalidInput] = useState(false);
  const [scenarioColumns, setScenarioColumns] = useState<ScenarioColumn[]>([]);
  const [oldScenarioTitle, setOldScenarioTitle] = useState<string>("");
  const [scenarioNewTitle, setScenarioNewTitle] = useState<string>("");
  const [pageErrorMessage, setPageErrorMessage] = useState<string | null>(null);

  const numberPattern = /^(?:100|\d{1,3})?$/;

  const navigate = useNavigate();

  const dynamicUrl = `/Location/${LocationId}/Reduction-Measures`;

  /**
   * Fetches the scenario data for the given location ID.
   * Groups the results by reduction measure ID and sets the rows and scenario keys.
   */
  const getScenarioByLocationId = async () => {
    try {
      setLoading(true);
      setPageErrorMessage(null);
      const res = await _getScenarioByLocationID(Number(LocationId));
      if (res) {
        
        setResponseState(res);

        let groupedResult = groupBy<ScenarioModel>(res, "reductionMeasureId");
        
 
        // Order each measure's scenarios by scenarioId to keep the matching
        // order deterministic for scenarios that share the same section name.
        const orderedByMeasure: { [id: string]: any[] } = {};
        Object.keys(groupedResult).forEach((id) => {
          orderedByMeasure[id] = [...groupedResult[id]].sort(
            (a: ScenarioModel, b: ScenarioModel) => a.scenarioId - b.scenarioId
          );
        });

        // Build columns from the measure with the most scenarios. Column keys
        // are label-independent so duplicate titles, or titles that look like
        // a disambiguated duplicate (for example "Plan A (2)"), cannot collide.
        let referenceScenarios: any[] = [];
        Object.values(orderedByMeasure).forEach((scenarios) => {
          if (scenarios.length > referenceScenarios.length) {
            referenceScenarios = scenarios;
          }
        });

        const columns: ScenarioColumn[] = referenceScenarios.map((scenario, index) => {
          const label = scenario.scenarioSection ?? "";
          const key = `scenario-column-${index}`;
          return { key, label };
        });

        setScenarioColumns(columns);
        setScenarioKeys(columns.map((column) => column.key));

        const updatedRows = Object.keys(orderedByMeasure).map(
          (reductionMeasureId) =>
            buildScenarioRow(orderedByMeasure[reductionMeasureId], columns)
        );
        setRows(updatedRows);
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not load decarbonization plans right now. Please refresh and try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "ScenarioView",
        action: "load scenarios",
        component: "ScenarioView",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: LocationId,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Groups an array of objects by a specified key.
   *
   * @template T
   * @param {T[]} collection - The array of objects to group.
   * @param {keyof T} key - The key to group by.
   * @returns {Record<string, T[]>} The grouped objects.
   */
  function groupBy<T>(collection: T[], key: keyof T) {
    const groupedResult = collection.reduce((previous, current) => {
      previous[current[key]] ??= [] as T[];
      previous[current[key]].push(current);
      return previous;
    }, {} as any);

    return groupedResult;
  }

  useEffect(() => {
    getScenarioByLocationId();
  }, []);

  /**
   * Updates the response state with new values for a specific scenario.
   *
   * @param {number} scenarioId - The unique ID of the scenario to update.
   * @param {object} updatedValues - The new values to update.
   */
  const updateResponseState = (scenarioId: number, updatedValues: any) => {
    setResponseState((prevState) =>
      prevState.map((scenario) =>
        scenario.scenarioId === scenarioId
          ? { ...scenario, ...updatedValues }
          : scenario
      )
    );
  };

  /**
   * Enable Edit scenatio Title option.
   * @param {string} columnKey - The unique key of the scenario column.
   * @param {string} label - The current section label of the scenario column.
   */
  const EditScenarioName = (columnKey: string, label: string) => {
    setOldScenarioTitle(columnKey);
    setScenarioNewTitle(label);
  };

  /**
   * Save the New scenario Title
   */
  const SaveScenarioName = async () => {
    const columnKey = oldScenarioTitle;

    // Collect the unique scenario IDs that belong to this column across every
    // measure so the rename targets exactly this column and never merges with a
    // different column that happens to share a name.
    const targetIds = new Set<number>(
      rows
        .map((row) => (row[columnKey] as Scenario)?.scenarioId)
        .filter((id): id is number => typeof id === "number")
    );

    const updatedScenarios = responseState
      .filter((scenario) => targetIds.has(scenario.scenarioId))
      .map((scenario) => ({
        ...scenario,
        scenarioSection: scenarioNewTitle,
      }));

    try {
      setPageErrorMessage(null);
      const res = await _addScenario(updatedScenarios);
      if (res) {
        setResponseState((prevState) =>
          prevState.map((scenario) =>
            targetIds.has(scenario.scenarioId)
              ? { ...scenario, scenarioSection: scenarioNewTitle }
              : scenario
          )
        );

        setScenarioColumns((prevColumns) =>
          prevColumns.map((column) =>
            column.key === columnKey
              ? { ...column, label: scenarioNewTitle }
              : column
          )
        );

        setOldScenarioTitle("");
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not update the plan name right now. Please try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "ScenarioView",
        action: "rename scenario",
        component: "ScenarioView",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: LocationId,
          oldScenarioTitle,
          scenarioNewTitle,
        },
      });
    }
  };

  /**
   * Handles the change event for execution percentage input fields.
   *
   * @param {string} scenarioKey - The key of the scenario.
   * @param {string} value - The new value of the input field.
   * @param {number} rowIndex - The index of the row being updated.
  * @param {number} scenarioId - The unique ID of the scenario being updated.
   */
  const handleExecutionPercentageChange = (
    scenarioKey: string,
    value: string,
    rowIndex: number,
    scenarioId: number
  ) => {
    if (value === "") {
      setExecution((prev) => ({
        ...prev,
        [`${scenarioKey}-${rowIndex}`]: "",
      }));
      updateResponseState(scenarioId, {
        execution: "",
      });
      setInvalidInput(false);
      return;
    }
    const numericValue = numericValidator.allowNumericValue(value);
    if (numberPattern.test(String(numericValue)) || value === "") {
      setExecution((prev) => ({
        ...prev,
        [`${scenarioKey}-${rowIndex}`]: String(numericValue),
      }));
      setInvalidInput(parseInt(numericValue.toString()) > 100);
      updateResponseState(scenarioId, {
        execution: String(numericValue),
      });
    }
  };

  /**
   * Handles the change event for pace input fields.
   *
   * @param {string} scenarioKey - The key of the scenario.
   * @param {string} value - The new value of the input field.
   * @param {number} rowIndex - The index of the row being updated.
  * @param {number} scenarioId - The unique ID of the scenario being updated.
   */
  const handlePeaceChange = (
    scenarioKey: string,
    value: string,
    rowIndex: number,
    scenarioId: number
  ) => {
    if (value === "") {
      setPeace((prev) => ({
        ...prev,
        [`${scenarioKey}-${rowIndex}`]: "",
      }));
      updateResponseState(scenarioId, { pace: "" });
      setInvalidInput(false);
      return;
    }

    const numericValue = numericValidator.allowNumericValue(value);
    if (numberPattern.test(String(numericValue)) || value === "") {
      setPeace((prev) => ({
        ...prev,
        [`${scenarioKey}-${rowIndex}`]: String(numericValue),
      }));
      setInvalidInput(parseInt(numericValue.toString()) > 100);
      updateResponseState(scenarioId, {
        pace: String(numericValue),
      });
    }
  };

  /**
   * Handles the change event for filter input fields.
   *
   * @param {string} scenarioKey - The key of the scenario.
   * @param {string | null | number} value - The new value of the input field.
   * @param {number} rowIndex - The index of the row being updated.
  * @param {number} scenarioId - The unique ID of the scenario being updated.
   */
  const handleFilterChange = (
    scenarioKey: string,
    value: string | null | number,
    rowIndex: number,
    scenarioId: number
  ) => {
    setStartYear((prev) => ({
      ...prev,
      [`${scenarioKey}-${rowIndex}`]: String(value),
    }));
    updateResponseState(scenarioId, {
      startYear: value,
    });
  };

  /**
   * Saves the current scenario state to the server.
   */
  const handleSave = async () => {
    try {
      setLoading(true);
      setPageErrorMessage(null);
      const res = await _addScenario(responseState);
      if (res) {
        setLoading(false);
        getScenarioByLocationId();
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not save decarbonization plans right now. Please try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "ScenarioView",
        action: "save scenarios",
        component: "ScenarioView",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: LocationId,
          scenarioCount: responseState.length,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets the state variables to their initial values.
   */
  const resetStateVariables = () => {
    setExecution({});
    setPeace({});
    setRows([]);
    setScenarioKeys([]);
    setScenarioColumns([]);
    setStartYear({});
    setResponseState([]);
    setLoading(true);
    setInvalidInput(false);
  };

  /**
   * Cancels any changes and reloads the scenario data.
   */
  const handleCancel = () => {
    resetStateVariables();
    getScenarioByLocationId();
  };

  /**
   * Redirects the user to the reduction measures page.
   */
  const handleRedirect = () => {
    navigate(dynamicUrl, { state: { triggerAddMeasure: true } });
  };

  /**
   * Renders the scenario table or an empty state if there are no rows.
   *
   * @returns {JSX.Element} The scenario table or empty state.
   */
  const renderScenarios = () => {
    if (rows.length > 0) {
      return (
        <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
        <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid item xs={12} sx={{ mb: 5 }}>
            <Paper variant="outlined">
              <Grid item sx={{ m: 1 }}>
                <div
                  style={{
                    fontFamily: "Inter",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#05004E",
                    width: "100%",
                    marginLeft: "8px",
                  }}
                >
                  Metrics
                </div>
              </Grid>
              <Grid item>
                <Paper variant="outlined">
                  <TableContainer style={{ overflowX: "auto", width: "100%", maxWidth: "100%" }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{ fontWeight: "bold", textAlign: "center" }}
                          >
                            Plans
                          </TableCell>
                          {scenarioColumns.map((column) => (
                            <React.Fragment key={column.key}>
                              <TableCell
                                colSpan={3}
                                sx={{
                                  fontWeight: "bold",
                                  textAlign: "center",
                                  padding: "0px !important",
                                }}
                              >
                                {oldScenarioTitle === "" ||
                                oldScenarioTitle !== column.key ? (
                                  <div id="view">
                                    {column.label}
                                    <EditIcon
                                      style={{
                                        color: "#e4610f",
                                        fontSize: 15,
                                        marginLeft: 10,
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        EditScenarioName(
                                          column.key,
                                          column.label
                                        )
                                      }
                                      data-testid={
                                        "editiconTestId" + column.key
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div
                                    id="edit"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <TextField
                                      required
                                      id={"Scenario" + column.key}
                                      inputProps={{
                                        "data-testid": "Scenario" + column.key,
                                      }}
                                      margin="none"
                                      size="small"
                                      style={{
                                        padding: "6px 10px",
                                        fontSize: 12,
                                        width: "80%",
                                      }}
                                      name={"Scenario" + column.key}
                                      value={scenarioNewTitle}
                                      onChange={(e) => {
                                        setScenarioNewTitle(e.target.value);
                                      }}
                                    />
                                    <CheckIcon
                                      style={{
                                        color: "green",
                                        fontSize: 25,
                                        marginLeft: 10,
                                        cursor: "pointer",
                                      }}
                                      onClick={() => SaveScenarioName()}
                                      data-testid={
                                        "SaveScenarioName" + column.key
                                      }
                                    />
                                  </div>
                                )}
                              </TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                          <TableCell
                            style={{
                              minWidth: "140px",
                              padding: "0px !important",
                            }}
                            sx={{ fontWeight: "bold" }}
                          >
                            Measure Name
                          </TableCell>
                          {scenarioKeys.map((scenarioKey) => (
                            <React.Fragment key={scenarioKey}>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  padding: "0px !important",
                                  textAlign: "center",
                                }}
                              >
                                Start Year
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  padding: "0px !important",
                                  textAlign: "center",
                                }}
                              >
                                Execution %
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  padding: "0px !important",
                                  textAlign: "center",
                                }}
                              >
                                Pace %
                              </TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, index) => (
                          <ScenarioTableRow
                            key={index + 1}
                            index={index}
                            row={row}
                            scenarioKeys={scenarioKeys}
                            startYear={startYear}
                            execution={execution}
                            peace={peace}
                            invalidInput={invalidInput}
                            handleExecutionPercentageChange={
                              handleExecutionPercentageChange
                            }
                            handlePeaceChange={handlePeaceChange}
                            handleFilterChange={handleFilterChange}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        </Grid>
      );
    } else if (!loading) {
      return <EmptyState handleRedirect={handleRedirect} />;
    }
  };

  return (
    <Box sx={{ width: 'calc(100vw - 320px)', maxWidth: 'calc(100vw - 320px)', overflow: 'visible', position: 'relative' }}>
      <Box
        sx={{
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          width: 'calc((100vw - 320px) / 0.75)',
          minHeight: '100vh',
        }}
      >
        <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%', p: 0, m: 0 }}>
          <ScenarioViewHeader />
          {pageErrorMessage && (
            <Grid item xs={12} sx={{ px: 2, mb: 1 }}>
              <Alert severity="error" data-testid="scenario-view-error-alert">
                {pageErrorMessage}
              </Alert>
            </Grid>
          )}
          {rows.length > 0 ? (
            <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
              <Grid container sx={{ width: '100%', maxWidth: '100%', mb: 1 }}>
                <Grid item xs={8.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "primary.main",
                      }}
                    >
                      Note:
                    </Typography>
                    <Typography
                      data-testid="blurb-message-info"
                      sx={{ p: 1, whiteSpace: "pre-line", ml: 1 }}
                    >
                      {TitleDefinitionConstants.ScenarioViewInfo}
                    </Typography>
                  </Box>
                </Grid>
                {userRole !== UserRoles.Viewers && (
                  <Grid xs={3} style={{ display: "flex", justifyContent: "flex-end" }} sx={{ mr: 3, mb: 3,mt:3 }}>
                    <Grid item>
                      <Button
                        type="submit"
                        data-testid="btnCancel"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item sx={{ ml: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        data-testid="btnSave"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ) : (
            ""
          )}

          {loading && <Spinner size={80} data-testid="spinner" />}

          {renderScenarios()}
        </Grid>
      </Box>
    </Box>
  );
};

export default ScenarioView;
